// import React, { useState, useMemo, useRef } from 'react';
// import { 
//     Box, Heading, Text, HStack, VStack, Card, CardHeader, CardBody, 
//     Icon, Tag, Select, IconButton, Button, Badge, Input, InputGroup, InputRightElement
// } from '@chakra-ui/react';
// import { ChevronLeft, ChevronRight, Calendar, Truck, Search } from 'lucide-react';
// import { AdminLayout } from '@/components/layout/AdminLayout';
// import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
// import { useThemeColors } from '@/hooks/useThemeColors';
// import { useAccessCheck } from '@/hooks/useAccess';
// import { useNavigate } from 'react-router-dom';

// const TransportSummaryReportPage: React.FC = () => {
//     const navigate = useNavigate();
//     const colors = useThemeColors();
//     const tableRef = useRef<DataTableRef>(null);
//     const searchRef = useRef<HTMLInputElement>(null); // 🌟 Added ref for search
//     const access = useAccessCheck();

//     const [filterType, setFilterType] = useState<'weekly' | 'monthly'>('weekly');
//     const [currentDate, setCurrentDate] = useState(new Date());
//     const [searchQuery, setSearchQuery] = useState('');

//     // --- 🌟 SEARCH HANDLERS ---
//     const handleSearch = () => {
//         setSearchQuery(searchRef.current?.value || '');
//     };

//     const handleKeyDown = (e: React.KeyboardEvent) => {
//         if (e.key === 'Enter') {
//             handleSearch();
//         }
//     };

//     // --- 🌟 DATE NAVIGATION LOGIC ---
//     const adjustDate = (direction: number) => {
//         const newDate = new Date(currentDate);
//         if (filterType === 'weekly') {
//             newDate.setDate(newDate.getDate() + (direction * 7));
//         } else {
//             newDate.setMonth(newDate.getMonth() + direction);
//         }
//         setCurrentDate(newDate);
//     };

//     const dateRangeDisplay = useMemo(() => {
//         const start = new Date(currentDate);
//         if (filterType === 'weekly') {
//             const day = start.getDay();
//             const diff = start.getDate() - day + (day === 0 ? -6 : 1); 
//             start.setDate(diff);
//             const end = new Date(start);
//             end.setDate(start.getDate() + 6);
//             return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
//         }
//         return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
//     }, [currentDate, filterType]);

//     const tableParams = useMemo(() => {
//         const start = new Date(currentDate);
//         const end = new Date(currentDate);
        
//         if (filterType === 'weekly') {
//             const day = start.getDay();
//             const diff = start.getDate() - day + (day === 0 ? -6 : 1);
//             start.setDate(diff);
//             end.setDate(start.getDate() + 6);
//         } else {
//             start.setDate(1);
//             end.setMonth(start.getMonth() + 1);
//             end.setDate(0);
//         }

//         return { 
//             filter_type: filterType, 
//             start_date: start.toISOString().split('T')[0],
//             end_date: end.toISOString().split('T')[0],
//             search: searchQuery 
//         };
//     }, [filterType, currentDate, searchQuery]);

//     const tableOptions: DataTableOptions = useMemo(
//         () => ({
//             dataSource: '/reports/transport-summary', 
//             primaryKey: 'truck_plate_number', 
//             dataKey: 'data',
//             showSelection: false,
//             showCheckboxes: false,
//             softDelete: false,
//             displayInModal: false,
//             showActionsColumn: false,
//             noRecordText: 'No transport summary found for this period',
//             pagination: true,
//             defaultPageSize: 15,
//             dataFields: {
//                 truck_plate_number: { name: 'TRUCK PLATE NUMBER', withSort: true },
//                 total_trips: { name: 'TOTAL TRIPS', withSort: true },
//                 total_expenses: { name: 'TOTAL EXPENSES', withSort: true },
//                 action_view: { name: 'ACTIONS', withSort: false },
//             },
//             actions: [],
//             otherActions: [{ name: 'View Details', link: '/analytics/transport-summary' }]
//         }),
//         [access.can_view]
//     );

//     const customRenderers = useMemo(() => ({
//         truck_plate_number: (val: string, row: any) => {
//             const plate = val || row?.truck_plate_number; 
//             return (
//                 <HStack 
//                     spacing={3} 
//                     onClick={() => {
//                         const params = new URLSearchParams({
//                             filter_type: filterType,
//                             start_date: tableParams.start_date,
//                             end_date: tableParams.end_date
//                         });
//                         navigate(`/analytics/transport-summary/${encodeURIComponent(plate)}?${params.toString()}`);
//                     }}
//                     cursor="pointer"
//                 >
//                     <Icon as={Truck} size={14} color="blue.500" />
//                     <Text fontWeight="bold" color="blue.600">{plate}</Text>
//                 </HStack>
//             );
//         },
//         total_trips: (val: number) => (
//             <Badge colorScheme={val > 10 ? "green" : "blue"} borderRadius="full" px={3}>
//                 {val} Trips
//             </Badge>
//         ),
//         total_expenses: (val: number) => (
//             <Text fontWeight="semibold">
//                 {new Intl.NumberFormat('en-PH', {
//                     style: 'currency', currency: 'PHP', minimumFractionDigits: 2
//                 }).format(val || 0)}
//             </Text>
//         ),
//         action_view: (_val: any, row: any) => (
//             <Button
//                 size="xs" colorScheme="blue" variant="outline"
//                 leftIcon={<Icon as={Truck} size={12} />}
//                 onClick={() => {
//                     const plate = row.truck_plate_number;
//                     const params = new URLSearchParams({
//                         filter_type: filterType,
//                         start_date: tableParams.start_date,
//                         end_date: tableParams.end_date
//                     });
//                     navigate(`/analytics/transport-summary/${plate}?${params.toString()}`);
//                 }}
//             >
//                 View Details
//             </Button>
//         ),
//     }), [filterType, tableParams, navigate]);

//     return (
//         <AdminLayout>
//             <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
//                 <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
//                     <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
//                         <CardHeader flexShrink={0} py={3}>
//                             <HStack justify="space-between" align="center">
//                                 <Box>
//                                     <Heading size="md" mb={1}>Transport Summary Report</Heading>
//                                     <Text fontSize="sm" color={colors.text}>Consolidated trip totals per truck plate number</Text>
//                                 </Box>
//                                 <Tag colorScheme="blue" variant="subtle" size="sm">{filterType.toUpperCase()} VIEW</Tag>
//                             </HStack>
//                         </CardHeader>

//                         <CardHeader py={0} px={0} flexShrink={0}>
//                             <VStack align="stretch" spacing={3} px={5} pb={3}>
//                                 <HStack justify="space-between" align="center" spacing={4} width="100%">
//                                     <HStack spacing={4}>
//                                         <Select 
//                                             size="sm" w="140px" borderRadius="md" 
//                                             value={filterType} 
//                                             onChange={(e) => setFilterType(e.target.value as any)}
//                                         >
//                                             <option value="weekly">Weekly</option>
//                                             <option value="monthly">Monthly</option>
//                                         </Select>
                                        
//                                         <HStack spacing={1}>
//                                             <IconButton icon={<ChevronLeft size={18} />} aria-label="Prev" size="sm" variant="ghost" onClick={() => adjustDate(-1)} />
//                                             <HStack 
//                                                 border="1px solid" borderColor="gray.200" px={4} py={1.5} borderRadius="md" 
//                                                 bg="white" cursor="pointer" position="relative" _hover={{ borderColor: "blue.400" }}
//                                                 onClick={() => (document.getElementById('report-date-picker') as HTMLInputElement)?.showPicker()}
//                                             >
//                                                 <Icon as={Calendar} size={14} color="blue.500" />
//                                                 <Text fontWeight="bold" fontSize="sm" minW="150px" textAlign="center">{dateRangeDisplay}</Text>
//                                                 <input id="report-date-picker" type="date" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
//                                                     onChange={(e) => e.target.value && setCurrentDate(new Date(e.target.value))}
//                                                 />
//                                             </HStack>
//                                             <IconButton icon={<ChevronRight size={18} />} aria-label="Next" size="sm" variant="ghost" onClick={() => adjustDate(1)} />
//                                         </HStack>
//                                     </HStack>
//                                 </HStack>

//                                 {/* 🌟 UPDATED SEARCH UI PATTERN */}
//                                 <Box flex="0 1 auto" minW="400px" maxW="400px">
//                                     <InputGroup size="sm">
//                                         <Input
//                                             ref={searchRef}
//                                             placeholder="Search trucks..."
//                                             onKeyDown={handleKeyDown}
//                                             defaultValue={searchQuery}
//                                             pr="2.5rem"
//                                         />
//                                         <InputRightElement width="2.5rem">
//                                             <IconButton
//                                                 aria-label="Search"
//                                                 icon={<Search size={16} />}
//                                                 size="sm"
//                                                 h="1.75rem"
//                                                 w="1.75rem"
//                                                 onClick={handleSearch}
//                                                 variant="ghost"
//                                             />
//                                         </InputRightElement>
//                                     </InputGroup>
//                                 </Box>
//                             </VStack>
//                         </CardHeader>
               
//                         <CardBody p={0} pt={1} pb={4} mx={5} flex="1" display="flex" flexDirection="column" minH={0} overflow="hidden">
//                             <Box h="100%" display="flex" flexDirection="column" minH={0}>
//                                 <DataTable
//                                     ref={tableRef}
//                                     options={tableOptions} 
//                                     params={tableParams}
//                                     customRenderers={customRenderers}
//                                     access={{ can_view: true, can_edit: false, can_create: false, can_delete: false }}
//                                 />
//                             </Box>
//                         </CardBody>
//                     </Card>
//                 </VStack> 
//             </Box> 
//         </AdminLayout>
//     );
// };

// export default TransportSummaryReportPage;

import React, { useState, useMemo, useRef } from 'react';
import { 
    Box, Heading, Text, HStack, VStack, Card, CardHeader, CardBody, 
    Icon, Tag, Select, IconButton, Button, Badge, Input, InputGroup, InputRightElement,
    useColorModeValue // 🌟 Added
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, Calendar, Truck, Search, FileDown } from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useThemeColors } from '@/hooks/useThemeColors';
import { useAccessCheck } from '@/hooks/useAccess';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiClient } from '@/lib/api/client';

const TransportSummaryReportPage: React.FC = () => {
    const navigate = useNavigate();
    const colors = useThemeColors();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const access = useAccessCheck();

    const [filterType, setFilterType] = useState<'weekly' | 'monthly'>('weekly');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [searchQuery, setSearchQuery] = useState('');

    // Exporting CSV and PDF
        const [isExporting, setIsExporting] = useState(false);
        const [isCsvExporting, setIsCsvExporting] = useState(false);
    
        const formatPHP = (val: number) => {
            const absoluteValue = Math.abs(val || 0);
            const formattedNumber = absoluteValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return 'P ' + formattedNumber;
        };
    
        const escapeCsvValue = (value: any) => {
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            return `"${stringValue.replace(/"/g, '""')}"`;
        };
    
        const fetchWithStrictRetry = async (url: string, params: any, attempt = 0): Promise<any> => {
            try {
                return await apiClient.get(url, { params });
            } catch (error: any) {
                if (error.response?.status === 429 && attempt < 3) {
                    await new Promise(r => setTimeout(r, 5000));
                    return fetchWithStrictRetry(url, params, attempt + 1);
                }
                throw error;
            }
        };
    
        const bgColor = useColorModeValue('white', 'gray.700');
        const borderColor = useColorModeValue('gray.200', 'gray.600');
        const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');
    
        const handleExportPDF = async () => {
            setIsExporting(true);
            const doc = new jsPDF('l', 'mm', 'a4');
            const margin = 14;
    
            const formatPHP = (val: number) => {
                const absoluteValue = Math.abs(val || 0);
                const formattedNumber = absoluteValue.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
                return "P " + formattedNumber; 
            };
    
            const fetchWithStrictRetry = async (url: string, params: any, attempt = 0): Promise<any> => {
                try {
                    return await apiClient.get(url, { params });
                } catch (error: any) {
                    if (error.response?.status === 429 && attempt < 3) {
                        await new Promise(r => setTimeout(r, 5000));
                        return fetchWithStrictRetry(url, params, attempt + 1);
                    }
                    throw error;
                }
            };
    
            const datesToExport: string[] = [];
            const selectedDate = new Date(currentDate); 
            
            if (filterType === 'weekly') {
                // 🌟 FIXED WEEKLY LOGIC: Anchor to Monday
                const day = selectedDate.getDay(); 
                // Adjust so that Monday is 0, Tuesday is 1... Sunday is 6
                const diff = selectedDate.getDate() - (day === 0 ? 6 : day - 1); 
                
                const monday = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), diff);
                
                for (let i = 0; i < 7; i++) {
                    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const dayStr = String(d.getDate()).padStart(2, '0');
                    datesToExport.push(`${y}-${m}-${dayStr}`);
                }
            } else {
                const year = selectedDate.getFullYear();
                const month = selectedDate.getMonth();
                const lastDay = new Date(year, month + 1, 0).getDate(); 
                
                for (let i = 1; i <= lastDay; i++) {
                    datesToExport.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`);
                }
            }
    
            try {
                for (let i = 0; i < datesToExport.length; i++) {
                    const dateStr = datesToExport[i];
    
                    // Cooldown to prevent rate limiting
                    if (i > 0 && i % 5 === 0) await new Promise(r => setTimeout(r, 2000));
    
                    const [transportSummaryRes] = await Promise.all([
                        fetchWithStrictRetry(`/reports/transport-summary`, { transaction_date: dateStr, per_page: 100 }),
                    ]);
                    
                    await new Promise(r => setTimeout(r, 600)); 
    
                    if (i > 0) doc.addPage();
    
                    doc.setFontSize(16); doc.setFont("helvetica", "bold");
                    doc.text(`Daily Operations: ${new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, margin, 20);
    
                    const sections = [
                        { title: 'Issued Budget', data: transportSummaryRes.data.data, head: [['TRUCK PLATE NUMBER', 'TOTAL TRIPS', 'TOTAL EXPENSES']], map: (item: any) => [item.truck_plate_number, item.total_trips, formatPHP(item.total_expenses)] },
                    ];
    
                    const hasAnyData = sections.some(s => s.data?.length > 0);
                    let currentY = 30;
    
                    if (!hasAnyData) {
                        doc.setFontSize(12); doc.setFont("helvetica", "italic");
                        doc.setTextColor(150, 150, 150);
                        doc.text("No Transactions recorded for this date.", margin, currentY);
                    } else {
                        doc.setTextColor(0, 0, 0);
                        sections.forEach(section => {
                            if (section.data?.length > 0) {
                                doc.setFontSize(11); doc.setFont("helvetica", "bold");
                                doc.text(section.title, margin, currentY);
                                autoTable(doc, {
                                    startY: currentY + 2,
                                    head: section.head,
                                    body: section.data.map(section.map),
                                    theme: 'striped',
                                    headStyles: { fillColor: [40, 40, 40] },
                                    margin: { left: margin },
                                });
                                currentY = (doc as any).lastAutoTable.finalY + 10;
                            }
                        });
                    }
                }
    
                doc.save(`Operations_Report_${dateRangeDisplay.replace(/\s+/g, '_')}.pdf`);
            } catch (error: any) {
                alert("Export failed. Please try again in a moment.");
            } finally {
                setIsExporting(false);
            }
        };
    
    const handleExportCSV = async () => {
        setIsCsvExporting(true);

        const getJsonParams = () => {
            const start = new Date(currentDate);
            const end = new Date(currentDate);

            if (filterType === 'weekly') {
                const day = start.getDay();
                const diff = start.getDate() - day + (day === 0 ? -6 : 1);
                start.setDate(diff);
                end.setDate(start.getDate() + 6);
            } else {
                start.setDate(1);
                end.setMonth(start.getMonth() + 1);
                end.setDate(0);
            }

            return {
                filter_type: filterType,
                start_date: start.toISOString().split('T')[0],
                end_date: end.toISOString().split('T')[0],
                search: searchQuery || undefined,
            };
        };

        try {
            const response = await apiClient.get('/reports/transport-summary/export', {
                params: getJsonParams(),
                responseType: 'blob',
            });

            const filename = `TruckSummary_${dateRangeDisplay.replace(/\s+/g, '_')}.csv`;
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Export CSV failed', error);
            alert('Export failed. Please try again in a moment.');
        } finally {
            setIsCsvExporting(false);
        }
    };

    // --- 🌟 DYNAMIC COLORS FOR DARK MODE ---
    const dateBoxBg = useColorModeValue('white', 'gray.700');
    const dateBoxBorder = useColorModeValue('gray.200', 'gray.600');
    const plateTextColor = useColorModeValue('blue.600', 'blue.300');
    const cardBg = useColorModeValue('white', 'gray.800');

    const handleSearch = () => {
        setSearchQuery(searchRef.current?.value || '');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const adjustDate = (direction: number) => {
        const newDate = new Date(currentDate);
        if (filterType === 'weekly') {
            newDate.setDate(newDate.getDate() + (direction * 7));
        } else {
            newDate.setMonth(newDate.getMonth() + direction);
        }
        setCurrentDate(newDate);
    };

    const dateRangeDisplay = useMemo(() => {
        const start = new Date(currentDate);
        if (filterType === 'weekly') {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1); 
            start.setDate(diff);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }, [currentDate, filterType]);

    const tableParams = useMemo(() => {
        const start = new Date(currentDate);
        const end = new Date(currentDate);
        
        if (filterType === 'weekly') {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1);
            start.setDate(diff);
            end.setDate(start.getDate() + 6);
        } else {
            start.setDate(1);
            end.setMonth(start.getMonth() + 1);
            end.setDate(0);
        }

        return { 
            filter_type: filterType, 
            start_date: start.toISOString().split('T')[0],
            end_date: end.toISOString().split('T')[0],
            search: searchQuery 
        };
    }, [filterType, currentDate, searchQuery]);

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataSource: '/reports/transport-summary', 
            primaryKey: 'truck_plate_number', 
            dataKey: 'data',
            showSelection: false,
            showCheckboxes: false,
            softDelete: false,
            displayInModal: false,
            showActionsColumn: false,
            noRecordText: 'No transport summary found for this period',
            pagination: true,
            defaultPageSize: 15,
            dataFields: {
                truck_plate_number: { name: 'TRUCK PLATE NUMBER', withSort: true },
                total_trips: { name: 'TOTAL TRIPS', withSort: true },
                total_expenses: { name: 'TOTAL EXPENSES', withSort: true },
                action_view: { name: 'ACTIONS', withSort: false },
            },
            actions: [],
            otherActions: [{ name: 'View Details', link: '/analytics/transport-summary' }]
        }),
        [access.can_view]
    );

    const customRenderers = useMemo(() => ({
        truck_plate_number: (val: string, row: any) => {
            const plate = val || row?.truck_plate_number; 
            return (
                <HStack 
                    spacing={3} 
                    onClick={() => {
                        const params = new URLSearchParams({
                            filter_type: filterType,
                            start_date: tableParams.start_date,
                            end_date: tableParams.end_date
                        });
                        navigate(`/analytics/transport-summary/${encodeURIComponent(plate)}?${params.toString()}`);
                    }}
                    cursor="pointer"
                >
                    <Icon as={Truck} size={14} color="blue.500" />
                    <Text fontWeight="bold" color={plateTextColor}>{plate}</Text> {/* FIXED: Dynamic color */}
                </HStack>
            );
        },
        total_trips: (val: number) => (
            <Badge colorScheme={val > 10 ? "green" : "blue"} borderRadius="full" px={3}>
                {val} Trips
            </Badge>
        ),
        total_expenses: (val: number) => (
            <Text fontWeight="semibold">
                {new Intl.NumberFormat('en-PH', {
                    style: 'currency', currency: 'PHP', minimumFractionDigits: 2
                }).format(val || 0)}
            </Text>
        ),
        action_view: (_val: any, row: any) => (
            <Button
                size="xs" colorScheme="blue" variant="outline"
                leftIcon={<Icon as={Truck} size={12} />}
                onClick={() => {
                    const plate = row.truck_plate_number;
                    const params = new URLSearchParams({
                        filter_type: filterType,
                        start_date: tableParams.start_date,
                        end_date: tableParams.end_date
                    });
                    navigate(`/analytics/transport-summary/${plate}?${params.toString()}`);
                }}
            >
                View Details
            </Button>
        ),
    }), [filterType, tableParams, navigate, plateTextColor]);

    return (
        <AdminLayout>
            <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
                <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
                    <Card 
                        bg={cardBg} // FIXED: Dynamic card background
                        flexShrink={0} 
                        display="flex" 
                        flexDirection="column" 
                        flex="1" 
                        minH={0} 
                        overflow="hidden"
                    >
                        
                        <CardHeader flexShrink={0} py={3}>
                            <HStack justify="space-between" align="center">
                                <Box>
                                    <Heading size="md" mb={1}>Transport Summary Report</Heading>
                                    <Text fontSize="sm" color={colors.text}>Consolidated trip totals per truck plate number</Text>
                                </Box>
                                <Tag colorScheme="blue" variant="subtle" size="sm">{filterType.toUpperCase()} VIEW</Tag>
                            </HStack>
                        </CardHeader>

                        <CardHeader py={0} px={0} flexShrink={0}>
                            <VStack align="stretch" spacing={3} px={5} pb={3}>
                                <HStack justify="space-between" align="center" spacing={4} width="100%">
                                    <HStack spacing={4}>
                                        <Select 
                                            size="sm" w="140px" borderRadius="md" 
                                            value={filterType} 
                                            onChange={(e) => setFilterType(e.target.value as any)}
                                        >
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </Select>
                                        
                                        <HStack spacing={1}>
                                            <IconButton icon={<ChevronLeft size={18} />} aria-label="Prev" size="sm" variant="ghost" onClick={() => adjustDate(-1)} />
                                            <HStack 
                                                border="1px solid" 
                                                borderColor={dateBoxBorder} // FIXED: Dynamic border
                                                px={4} 
                                                py={1.5} 
                                                borderRadius="md" 
                                                bg={dateBoxBg} // FIXED: Dynamic background
                                                cursor="pointer" 
                                                position="relative" 
                                                _hover={{ borderColor: "blue.400" }}
                                                onClick={() => (document.getElementById('report-date-picker') as HTMLInputElement)?.showPicker()}
                                            >
                                                <Icon as={Calendar} size={14} color="blue.500" />
                                                <Text fontWeight="bold" fontSize="sm" minW="150px" textAlign="center">
                                                    {dateRangeDisplay}
                                                </Text>
                                                <input id="report-date-picker" type="date" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                                                    onChange={(e) => e.target.value && setCurrentDate(new Date(e.target.value))}
                                                />
                                            </HStack>
                                            <IconButton icon={<ChevronRight size={18} />} aria-label="Next" size="sm" variant="ghost" onClick={() => adjustDate(1)} />
                                        </HStack>
                                    </HStack>
                                </HStack>

                                <HStack justify="space-between" align="center" width="100%">
                                    <Box flex="0 1 auto" minW="400px" maxW="400px">
                                        <InputGroup size="sm">
                                            <Input
                                                ref={searchRef}
                                                placeholder="Search trucks..."
                                                onKeyDown={handleKeyDown}
                                                defaultValue={searchQuery}
                                                pr="2.5rem"
                                            />
                                            <InputRightElement width="2.5rem">
                                                <IconButton
                                                    aria-label="Search"
                                                    icon={<Search size={16} />}
                                                    size="sm"
                                                    h="1.75rem"
                                                    w="1.75rem"
                                                    onClick={handleSearch}
                                                    variant="ghost"
                                                />
                                            </InputRightElement>
                                        </InputGroup>
                                    </Box>

                                    <HStack>
                                        {/* 🌟 PATTERNED BUTTON: Matches "Create New Truck" style */}
                                        <Button 
                                            leftIcon={<FileDown size={18} />} 
                                            colorScheme="blue" 
                                            size="sm"
                                            px={4}
                                            fontWeight="semibold"
                                            onClick={handleExportCSV}
                                            isLoading={isCsvExporting}
                                            loadingText="Generating..."
                                        >
                                            Export CSV
                                        </Button>
                                        <Button 
                                            leftIcon={<FileDown size={18} />} 
                                            colorScheme="blue" 
                                            size="sm"
                                            px={4}
                                            fontWeight="semibold"
                                            onClick={handleExportPDF}
                                            isLoading={isExporting}
                                            loadingText="Generating..."
                                        >
                                            Export PDF
                                        </Button>
                                    </HStack>
                                </HStack>
                            </VStack>
                        </CardHeader>
               
                        <CardBody p={0} pt={1} pb={4} mx={5} flex="1" display="flex" flexDirection="column" minH={0} overflow="hidden">
                            <Box h="100%" display="flex" flexDirection="column" minH={0}>
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions} 
                                    params={tableParams}
                                    customRenderers={customRenderers}
                                    access={{ can_view: true, can_edit: false, can_create: false, can_delete: false }}
                                />
                            </Box>
                        </CardBody>
                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default TransportSummaryReportPage;